import MainContainer from "components/layout/MainContainer/MainContainer";
import Integrations from "./Integrations";
import { PageContainer } from "components/UI/Structure/PageContainer";
import Typography from "@mui/material/Typography";
import StyledCard, { PlaceholderWrapper } from "components/UI/StyledCard";
import { useNavigate, useParams } from "react-router-dom";
import { ClientRecord, ContractRecord, getRecordIdentity, VendorRecord } from "lib/DataModel";
import { useEffect, useMemo, useState } from "react";
import ExpandingTextField from "components/UI/ExpandingTextField/ExpandingTextField";
import { Form, getIn, useFormikContext } from "components/Formik/forms";
import FooterBar from "components/UI/FooterBar";
import BookendsProvider, { useBookends } from "lib/BookendsProvider";
import { UnsavedChangesDialog } from "../LandingPages/shared/CancelOrConfirmDialogs";
import { useOrbit } from "providers/OrbitProvider";
import ContractResource from 'resources/ContractResource'
import FieldsResource from 'resources/FieldsResource'
import ClientCampaignsResource from 'resources/ClientCampaignsResource'
import DegreeProgramsResource from 'resources/DegreeProgramsResource'
import ClientExclusionRulesResource from 'resources/ClientExclusionRulesResource'
import Suspenseful from "components/Suspenseful";


function VendorNotesForm() {
  const { dirty, isSubmitting, submitForm, resetForm, errors } = useFormikContext()
  const { setBookends } = useBookends()
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    setBookends({
      footer: {
        buttons: [
          {
            type: "secondary",
            label: "Cancel",
            disabled: !dirty,
            onClick: () => {
              setCancelling(true)
            },
          },
          {
            type: "primary",
            label: isSubmitting ? 'Please wait...' : 'Save',
            disabled: !dirty || isSubmitting,
            onClick: submitForm,
          },
        ],
      },
      state: {
        form: {
          dirty: dirty,
          errors: errors,
        },
      },
    })
  }, [dirty, errors, isSubmitting, setBookends, submitForm, resetForm])

  return (
    <>
      <ExpandingTextField label='Notes' name='vendorNotes' inactiveRows={8}/>

      {cancelling &&
        <UnsavedChangesDialog
          onCancel={() => setCancelling(false)}
          onConfirm={() => { resetForm(); setCancelling(false) }}
        />
      }
    </>
  )
}

function VendorNotesSection({ contractResource }) {
  const { store } =  useOrbit()
  const { contractId } = useParams()
  const contractFromParams = contractId ? store.cache.query<ContractRecord>(q => q.findRecord(getRecordIdentity('contract', contractId))) : null
  const [contract, setContract] = useState(contractResource.get())

  const client = useMemo(() => contract && store.cache.query<ClientRecord>(q => q.findRelatedRecord(contract, 'client')), [contract, store])
  const clientName = getIn(client, 'attributes.name')
  const vendor = useMemo(() => contract && store.cache.query<VendorRecord>(q => q.findRelatedRecord(contract, 'vendor')), [contract, store])
  const vendorName = getIn(vendor, 'attributes.name')

  // Set contract if contract from params changes
  useEffect(() => {
    setContract(contractFromParams)
  }, [contractFromParams])

  return (
    <StyledCard
      title='Notes'
      note={`Use this section for notes about ${clientName}. These notes are only visible to ${vendorName} users.`}
      isSubsection={true}
    >
      <PlaceholderWrapper>
        {contract &&
          <Form resource={contract} setResource={setContract} backwardsCompatibleMargins={true} withoutSubmitButton>
            <VendorNotesForm />
          </Form>
        }
      </PlaceholderWrapper>
    </StyledCard>
  )
}

export default function VendorIntegrationPage() {
  const { remote, store } = useOrbit()
  const { contractId } = useParams()
  const contractFromRoute = contractId ? store.cache.query<ContractRecord>(q => q.findRecord(getRecordIdentity('contract', contractId))) : null
  const contractResource = useMemo(() => new ContractResource({ remote, id: contractId }), [remote, contractId])
  const fieldsResource = useMemo(() => contractFromRoute && new FieldsResource(remote, contractFromRoute), [remote, contractFromRoute])
  const clientCampaignsResource = useMemo(() => contractFromRoute && new ClientCampaignsResource({remote, contractOrClient: contractFromRoute}), [remote, contractFromRoute])
  const degreeProgramsResource = useMemo(() => contractFromRoute && new DegreeProgramsResource({ remote, contract: contractFromRoute, includeExclusionRules: true }), [remote, contractFromRoute])
  const client = useMemo(() => contractFromRoute && store.cache.query(q => q.findRelatedRecord(contractFromRoute, 'client')), [contractFromRoute, store])
  const clientExclusionRulesResource = useMemo(() => client && new ClientExclusionRulesResource({ remote, client: client}), [remote, client])

  const navigate = useNavigate();
  useEffect(() => {
    if(!contractFromRoute?.attributes?.canPostLeads) {
      navigate(`/contracts/${contractId}/dashboard`)
    }
  }, [navigate, contractFromRoute?.attributes?.canPostLeads, contractId])

  return (
    <BookendsProvider>
      <MainContainer>
        <PageContainer>
          <Typography variant='h1'>Form Posting Instructions</Typography>
          <Suspenseful
            component={Integrations}
            contractResource={contractResource}
            clientCampaignsResource={clientCampaignsResource}
            degreeProgramsResource={degreeProgramsResource}
            clientExclusionRulesResource={clientExclusionRulesResource}
            fieldsResource={fieldsResource}
          />
          <Suspenseful
            component={VendorNotesSection}
            contractResource={contractResource}
          />
        </PageContainer>
      </MainContainer>

      <FooterBar />
    </BookendsProvider>
  )
}
